import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { trackAppFeature } from "@/analytics/app-analytics";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type { Ref } from "vue";
import type {
  AudioRecording,
  CreateAudioRecordingPayload,
  CreateFieldObservationPayload,
  CreateFieldPartPayload,
  CreateFieldPartTemplatePayload,
  FieldObservation,
  FieldPart,
  FieldPartTemplate,
  SyncFieldObservationPayload,
  UpdateAudioRecordingPayload,
  UpdateFieldObservationPayload,
  UpdateFieldPartPayload,
  UpdateFieldPartTemplatePayload,
} from "@/types/field-observations.types";
import { useToast } from "@/composable/useToast";
import { useAIGuardError } from "@/composable/useAIGuardError";
import { useTranslation } from "i18next-vue";

// ==========================================
// Field Observations
// ==========================================

export const useGetFieldObservations = (diagnosticId: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FIELD_OBSERVATIONS, { diagnosticId }],
    queryFn: async () => {
      return await APIClient.getFieldObservations(diagnosticId.value);
    },
    enabled: () => diagnosticId.value > 0,
  });
};

export const useGetFieldObservation = (id: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FIELD_OBSERVATIONS, { id }],
    queryFn: async () => {
      return await APIClient.getFieldObservation(id.value);
    },
    enabled: () => id.value > 0,
  });
};

export const useCreateFieldObservation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: CreateFieldObservationPayload) => {
      return await APIClient.createFieldObservation(payload);
    },
    onSuccess: (observation: FieldObservation) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.FIELD_OBSERVATIONS,
          { diagnosticId: observation.diagnosticId },
        ],
      });
    },
    onError: createErrorHandler(t("toast:fieldObservations.createError")),
  });
};

export const useUpdateFieldObservation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateFieldObservationPayload;
    }) => {
      return await APIClient.updateFieldObservation(id, payload);
    },
    onSuccess: (observation: FieldObservation) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.FIELD_OBSERVATIONS,
          { diagnosticId: observation.diagnosticId },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS, { id: observation.id }],
      });
    },
    onError: createErrorHandler(t("toast:fieldObservations.updateError")),
  });
};

export const useDeleteFieldObservation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      id,
      diagnosticId,
    }: {
      id: number;
      diagnosticId: number;
    }) => {
      await APIClient.deleteFieldObservation(id);
      return { diagnosticId };
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS, { diagnosticId }],
      });
    },
    onError: createErrorHandler(t("toast:fieldObservations.deleteError")),
  });
};

export const useSyncFieldObservations = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      observations,
      diagnosticId,
    }: {
      observations: SyncFieldObservationPayload[];
      diagnosticId: number;
    }) => {
      const result = await APIClient.syncFieldObservations(observations);
      return { result, diagnosticId };
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS, { diagnosticId }],
      });
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.syncError"));
    },
  });
};

// ==========================================
// Audio Recordings
// ==========================================

export const useGetAudioRecordings = (observationId: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.AUDIO_RECORDINGS, { observationId }],
    queryFn: async () => {
      return await APIClient.getAudioRecordings(observationId.value);
    },
    enabled: () => observationId.value > 0,
  });
};

export const useCreateAudioRecording = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateAudioRecordingPayload) => {
      return await APIClient.createAudioRecording(payload);
    },
    onSuccess: (audioRecording: AudioRecording) => {
      // Invalidate observation queries to refresh the audio recordings list
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.AUDIO_RECORDINGS,
          { observationId: audioRecording.observationId },
        ],
      });
      // Also invalidate the parent observation
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS],
      });
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.audioCreateError"));
    },
  });
};

export const useUpdateAudioRecording = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateAudioRecordingPayload;
      observationId: number;
    }) => {
      return await APIClient.updateAudioRecording(id, payload);
    },
    onSuccess: (_, { observationId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.AUDIO_RECORDINGS, { observationId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS],
      });
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.audioUpdateError"));
    },
  });
};

export const useDeleteAudioRecording = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      id,
      observationId,
    }: {
      id: number;
      observationId: number;
    }) => {
      await APIClient.deleteAudioRecording(id);
      return { observationId };
    },
    onSuccess: (_, { observationId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.AUDIO_RECORDINGS, { observationId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS],
      });
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.audioDeleteError"));
    },
  });
};

// ==========================================
// Field Parts
// ==========================================

export const useGetFieldParts = (diagnosticId: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FIELD_PARTS, { diagnosticId }],
    queryFn: async () => {
      return await APIClient.getFieldParts(diagnosticId.value);
    },
    enabled: () => diagnosticId.value > 0,
  });
};

export const useCreateFieldPart = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateFieldPartPayload) => {
      return await APIClient.createFieldPart(payload);
    },
    onSuccess: (part: FieldPart) => {
      trackAppFeature("field_part_created");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId: part.diagnosticId }],
      });
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.partCreateError"));
    },
  });
};

export const useUpdateFieldPart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateFieldPartPayload;
      diagnosticId: number;
    }) => {
      return await APIClient.updateFieldPart(id, payload);
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId }],
      });
    },
  });
};

export const useDeleteFieldPart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      diagnosticId,
    }: {
      id: number;
      diagnosticId: number;
    }) => {
      await APIClient.deleteFieldPart(id);
      return { diagnosticId };
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId }],
      });
    },
  });
};

export const useDuplicateFieldPart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      names,
      diagnosticId,
    }: {
      id: number;
      names: string[];
      diagnosticId: number;
    }) => {
      const result = await APIClient.duplicateFieldPart(id, names);
      return { result, diagnosticId };
    },
    onSuccess: (_, { diagnosticId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId }],
      });
    },
  });
};

export const useApplyFieldPartTemplate = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      diagnosticId,
      templateId,
      parentPartId,
    }: {
      diagnosticId: number;
      templateId: number;
      parentPartId?: number;
    }) => {
      return await APIClient.applyFieldPartTemplate(
        diagnosticId,
        templateId,
        parentPartId,
      );
    },
    onSuccess: (parts: FieldPart[]) => {
      trackAppFeature("field_part_template_used");
      if (parts.length > 0) {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.FIELD_PARTS,
            { diagnosticId: parts[0].diagnosticId },
          ],
        });
      }
    },
    onError: () => {
      showWarning(t("toast:fieldObservations.applyTemplateError"));
    },
  });
};

// ==========================================
// Field Part Templates
// ==========================================

export const useGetFieldPartTemplates = () => {
  return useQuery({
    queryKey: [QueryKey.FIELD_PART_TEMPLATES],
    queryFn: async () => {
      return await APIClient.fetchFieldPartTemplates();
    },
  });
};

export const useGetFieldPartTemplate = (id: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FIELD_PART_TEMPLATES, { id }],
    queryFn: async () => {
      return await APIClient.fetchFieldPartTemplate(id.value);
    },
    enabled: () => id.value > 0,
  });
};

export const useCreateFieldPartTemplate = () => {
  const queryClient = useQueryClient();
  const { showWarning, showSuccess } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateFieldPartTemplatePayload) => {
      return await APIClient.createFieldPartTemplate(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PART_TEMPLATES],
      });
      showSuccess(t("toast:fieldPartTemplates.createSuccess"));
    },
    onError: () => {
      showWarning(t("toast:fieldPartTemplates.createError"));
    },
  });
};

export const useUpdateFieldPartTemplate = () => {
  const queryClient = useQueryClient();
  const { showWarning, showSuccess } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateFieldPartTemplatePayload;
    }) => {
      return await APIClient.updateFieldPartTemplate(id, payload);
    },
    onSuccess: (template: FieldPartTemplate) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PART_TEMPLATES],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PART_TEMPLATES, { id: template.id }],
      });
      showSuccess(t("toast:fieldPartTemplates.updateSuccess"));
    },
    onError: () => {
      showWarning(t("toast:fieldPartTemplates.updateError"));
    },
  });
};

export const useDeleteFieldPartTemplate = () => {
  const queryClient = useQueryClient();
  const { showWarning, showSuccess } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (id: number) => {
      await APIClient.deleteFieldPartTemplate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PART_TEMPLATES],
      });
      showSuccess(t("toast:fieldPartTemplates.deleteSuccess"));
    },
    onError: () => {
      showWarning(t("toast:fieldPartTemplates.deleteError"));
    },
  });
};
